import React, { useState, useEffect } from "react";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Loader2, Bell, CheckCheck } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

export default function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data } = await digitalOcean.auth.getSession();
      const session = data?.session;
      if (!session) {
        navigate(createPageUrl('Auth'));
        return;
      }

      const { data: profile } = await digitalOcean
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', currentUser?.id],
    queryFn: async () => {
      const { data } = await digitalOcean
        .from('notifications')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!currentUser,
  });

  // Setup realtime subscription for new notifications
  useEffect(() => {
    if (!currentUser) return;

    const channel = digitalOcean
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${currentUser.id}`
        },
        () => {
          // Refetch notifications when a new one is added
          queryClient.invalidateQueries(['notifications']);
        }
      )
      .subscribe();

    return () => {
      digitalOcean.removeChannel(channel);
    };
  }, [currentUser, queryClient]);

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      await digitalOcean
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await digitalOcean
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', currentUser.id)
        .eq('is_read', false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    },
  });

  if (!currentUser || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#707070]" />
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-[#a0a0a0]" />
            <h1 className="text-3xl font-light text-[#d0d0d0]">Notifications</h1>
            {unreadCount > 0 && (
              <span className="neuro-pressed px-3 py-1 rounded-full text-xs text-[#a0a0a0]">
                {unreadCount} new
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              onClick={() => markAllAsReadMutation.mutate()}
              className="neuro-base active:neuro-pressed rounded-2xl px-4 py-2"
              tooltip="Mark all notifications as read"
            >
              <CheckCheck className="w-4 h-4 text-[#a0a0a0] mr-2" />
              <span className="text-[#a0a0a0]">Mark all read</span>
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="neuro-base rounded-3xl p-12 text-center">
            <Bell className="w-12 h-12 text-[#606060] mx-auto mb-4" />
            <p className="text-sm text-[#808080]">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`neuro-base rounded-2xl p-4 ${
                  !notification.is_read ? 'border-l-4 border-[#808080]' : ''
                }`}
                onClick={() => {
                  if (!notification.is_read) {
                    markAsReadMutation.mutate(notification.id);
                  }
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-sm font-medium text-[#d0d0d0]">
                    {notification.title}
                  </h3>
                  <span className="text-xs text-[#606060]">
                    {format(new Date(notification.created_at), 'MMM d, h:mm a')}
                  </span>
                </div>
                <p className="text-xs text-[#a0a0a0]">{notification.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
